import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { Store } from '@ngrx/store';

import { OrderShipmentAttachmentsComponent } from '@components/orders/manage-orders/production-flow/order-shipping-details/order-additional-shipments/order-shipment-attachments/order-shipment-attachments.component';
import { OrderSingleAttachmentComponent } from '@components/orders/manage-orders/production-flow/order-shipping-details/order-additional-shipments/order-single-attachment/order-single-attachment.component';
import { Observable } from 'rxjs';

import { Country } from '../../../../../../@store/clients/clients.state.interface';
import { OrdersActions } from '../../../../../../@store/orders/orders.actions';
import { singleOrderDetails } from '../../../../../../@store/orders/orders.model';
import { selectPackingSlipCategories } from '../../../../../../@store/orders/orders.selectors';
import { QuoteStatus } from '../../../../../../@store/quotes/quotes.state.interface';
import { selectIsOrdersManager } from '../../../../../../@store/user/user.selectors';

@Component({
  selector: 'ciwc-order-additional-shipments',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    CommonModule,
    MatButton,
    MatDatepickerModule,
    MatNativeDateModule,
    MatOption,
    MatSelect,
    MatIcon,
    MatIconButton,
    OrderShipmentAttachmentsComponent,
    OrderSingleAttachmentComponent,
  ],
  standalone: true,
  templateUrl: './order-additional-shipments.component.html',
  styleUrl: './order-additional-shipments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAdditionalShipmentsComponent implements OnInit {
  private readonly store = inject(Store);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);

  @Input() parentForm!: FormGroup;
  @Input() orderDetails!: singleOrderDetails;
  @Input() createShipmentGroup!: () => FormGroup;
  @Input() countries!: Country[] | undefined;
  @Input() viewDisable = false;

  packingSlipCategories$: Observable<QuoteStatus[]> = this.store.select(
    selectPackingSlipCategories,
  );

  get shipments(): FormArray {
    return this.parentForm.get('shipments') as FormArray;
  }

  ngOnInit(): void {
    if (!this.viewDisable) {
      this.store.dispatch(
        OrdersActions.loadPackingSlipCategories({
          payload: { identifier: 'ORDERS_PACKING_SLIP_CATEGORIES' },
        }),
      );
    }

    if (typeof this.createShipmentGroup === 'function') {
      // Clear existing shipments
      while (this.shipments.length) {
        this.shipments.removeAt(0);
      }
      // If orderDetails.shipments exists, populate the form array
      if (
        this.orderDetails &&
        Array.isArray(this.orderDetails.shipments) &&
        this.orderDetails.shipments.length > 0
      ) {
        this.orderDetails.shipments.forEach((shipment) => {
          const group = this.createShipmentGroup();
          group.patchValue({
            ...shipment,
            dateShipped: shipment.dateShipped ? new Date(shipment.dateShipped) : null,
          });
          this.shipments.push(group);
          this.attachShipmentUnitListener(group);
        });
      } else {
        // If no shipments, add a blank one
        this.addShipment();
      }
      // Set first shipment unitsAvailableToShip from order totals on init
      this.prefillFirstShipmentAvailableUnits();
      // Subscribe to job details changes to recalc totals and prefill
      this.attachJobDetailsRecalcListeners();
    } else {
      console.error('createShipmentGroup function is required for shipments.');
    }
  }

  addShipment(): void {
    if (typeof this.createShipmentGroup === 'function') {
      const group = this.createShipmentGroup();
      // Prefill remaining totalPrice and unitsAvailableToShip
      const remainingTotal = this.calculateRemainingOrderTotal();
      group.get('totalPrice')?.setValue(remainingTotal);
      const last = this.shipments.length ? (this.shipments.at(this.shipments.length - 1) as FormGroup) : null;
      const prevAvail = Number(last?.get('unitsAvailableToShip')?.value || 0);
      const prevUnits = Number(last?.get('units')?.value || 0);
      const newAvail = Math.max(prevAvail - prevUnits, 0);
      group.get('unitsAvailableToShip')?.setValue(String(newAvail));

      this.attachShipmentUnitListener(group);
      this.shipments.push(group);
    } else {
      console.error('createShipmentGroup function is required for shipments.');
    }
  }

  trackByIndex(index: number, _item: any): number {
    return index;
  }

  getShipmentGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  trackByCountryName(index: number, country: { name: string }): string {
    return country.name;
  }

  removeShipment(index: number): void {
    this.shipments.removeAt(index);
  }

  sentEmail() {
    // dispatch action to send email
    throw new Error('Method not implemented.');
  }

  private attachJobDetailsRecalcListeners(): void {
    const job = this.parentForm.get('jobDetails');
    if (!job) return;

    const recalcKeys = [
      'pricePerUnit',
      'totalQuantity',
      'orderTotalPrice',
      'expeditedOrder',
      'expeditePrice',
      'faiReport',
      'faiReportPrice',
    ];
    recalcKeys.forEach((key) => {
      job.get(key)?.valueChanges.subscribe(() => {
        this.prefillFirstShipmentAvailableUnits();
        this.recalculateAllShipmentTotals();
      });
    });
  }

  private prefillFirstShipmentAvailableUnits(): void {
    const job = this.parentForm.get('jobDetails');
    if (!job || this.shipments.length === 0) return;

    const unit = this.orderDetails?.unit;
    const totalQuantity = Number(job.get('totalQuantity')?.value || 0);
    const sections = Number(this.orderDetails?.sections || 0);

    const first = this.shipments.at(0) as FormGroup;
    const value = unit === 'EA' ? sections : totalQuantity;
    first.get('unitsAvailableToShip')?.setValue(String(value));
  }

  private recalculateAllShipmentTotals(): void {
    for (let i = 0; i < this.shipments.length; i++) {
      const group = this.shipments.at(i) as FormGroup;
      this.recalculateShipmentTotal(group);
    }
  }

  private attachShipmentUnitListener(group: FormGroup): void {
    group.get('units')?.valueChanges.subscribe(() => this.recalculateShipmentTotal(group));
  }

  private recalculateShipmentTotal(group: FormGroup): void {
    const job = this.parentForm.get('jobDetails');
    if (!job) return;

    const unitType = this.orderDetails?.unit;
    const units = Number(group.get('units')?.value || 0);
    const orderTotalPrice = Number(job.get('orderTotalPrice')?.value || 0);
    const pricePerUnit = Number(job.get('pricePerUnit')?.value || 0);
    const totalQuantity = Number(job.get('totalQuantity')?.value || 0);
    const sections = Number(this.orderDetails?.sections || 0);

    let total = 0;
    if (unitType === 'EA') {
      total = units * pricePerUnit;
    } else {
      const denominator = unitType === 'EA' ? sections : totalQuantity;
      total = denominator ? (units * orderTotalPrice) / denominator : 0;
    }

    group.get('totalPrice')?.setValue(Number.isFinite(total) ? +total.toFixed(2) : 0, {
      emitEvent: false,
    });
  }

  private calculateRemainingOrderTotal(): number {
    const job = this.parentForm.get('jobDetails');
    const orderTotalPrice = Number(job?.get('orderTotalPrice')?.value || 0);
    const previousSum = this.shipments.controls.reduce((sum, ctrl) => {
      const v = Number((ctrl as FormGroup).get('totalPrice')?.value || 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
    const remaining = Math.max(orderTotalPrice - previousSum, 0);
    return +remaining.toFixed(2);
  }
}
